import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Minimize2, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

// Error Boundary Component
class ChatErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ChatWidget Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed bottom-4 right-4 w-96 bg-white rounded-lg shadow-2xl p-6 border-2 border-red-300">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h3 className="font-bold text-gray-900">Chat Error</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            We're experiencing technical difficulties with the support chat. Please try refreshing the page or contact us at support@constantloveco.com
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="w-full bg-rose-500 hover:bg-rose-600"
          >
            Refresh Page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ChatWidget = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Conchi, your Support Agent. I'm here to help you with any questions about Constant Love - features, subscriptions, troubleshooting, or anything else! How can I assist you today? 💕",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${API_BASE_URL}/api/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          conversation_history: messages.slice(-10) // Send last 10 messages for context
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Support chat error:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      // Validate response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format');
      }
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response || "I received your message but couldn't generate a proper response. Please try rephrasing your question.",
        timestamp: new Date(),
        helpLinks: Array.isArray(data.help_links) ? data.help_links : []
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      let errorMsg = "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or email us at support@constantloveco.com for immediate assistance.";
      
      if (error.name === 'AbortError') {
        errorMsg = "The request timed out. Please try again with a shorter message.";
      }
      
      const errorMessage = {
        role: 'assistant',
        content: errorMsg,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { label: 'How do I upgrade?', query: 'How do I upgrade to the Growth plan?' },
    { label: 'Cancel subscription', query: 'How do I cancel my subscription?' },
    { label: 'Install app', query: 'How do I install Constant Love on my phone?' },
    { label: 'Reset password', query: 'How do I reset my password?' },
  ];

  const handleQuickAction = (query) => {
    setInputMessage(query);
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center z-50 group"
        aria-label="Open support chat"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
      </button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl flex flex-col z-50 border-rose-200 bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">Support by Conchi</h3>
            <p className="text-xs text-rose-100">Usually replies instantly</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onToggle}
            className="hover:bg-white/20 p-1 rounded transition-colors"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-rose-50/30 to-white"
      >
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === 'user'
                  ? 'bg-rose-500 text-white'
                  : 'bg-white border border-rose-200 text-gray-800'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              {message.helpLinks && message.helpLinks.length > 0 && (
                <div className="mt-2 pt-2 border-t border-rose-200 space-y-1">
                  {message.helpLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      className="block text-xs text-rose-600 hover:text-rose-800 underline"
                    >
                      📚 {link.title}
                    </a>
                  ))}
                </div>
              )}
              <p className="text-xs opacity-70 mt-1">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-rose-200 rounded-lg p-3">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {messages.length === 1 && (
        <div className="px-4 py-2 bg-rose-50/50 border-t border-rose-200">
          <p className="text-xs text-gray-600 mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickAction(action.query)}
                className="text-xs bg-white border border-rose-300 text-rose-700 px-3 py-1 rounded-full hover:bg-rose-50 transition-colors"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Actions - End/Restart */}
      {messages.length > 1 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <div className="flex gap-2 justify-between items-center">
            <button
              onClick={() => {
                if (window.confirm('Start a new ticket? This will clear the current conversation.')) {
                  setMessages([{
                    role: 'assistant',
                    content: "Hi! I'm the Constant Love support assistant. How can I help you today?",
                    timestamp: new Date()
                  }]);
                }
              }}
              className="text-xs bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              🎫 New Ticket
            </button>
            <a
              href="mailto:support@constantloveco.com"
              className="text-xs bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              📧 Contact Human Support
            </a>
            <button
              onClick={onToggle}
              className="text-xs bg-rose-500 text-white px-3 py-2 rounded-lg hover:bg-rose-600 transition-colors"
            >
              ✓ End Chat
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-rose-200 bg-white">
        <div className="flex items-end space-x-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your question..."
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 max-h-24"
            rows="2"
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white h-10 w-10 p-0 flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Powered by AI • For urgent issues: support@constantloveco.com
        </p>
      </div>
    </Card>
  );
};

// Wrap with Error Boundary
const ChatWidgetWithErrorBoundary = (props) => (
  <ChatErrorBoundary>
    <ChatWidget {...props} />
  </ChatErrorBoundary>
);

export default ChatWidgetWithErrorBoundary;
